import { BaseModal, FormActions, FormField, FormInput, FormSectionTitle, FormSelect } from '@practium/ui';
import {
	PARAM_LABELS,
	SOIL_GROUP_CODES,
	SOIL_KIND_LABELS,
	TEXTURE_LABELS,
} from '../../constants/parameters.constants';
import { useSoilRuleForm, usesBand } from '../../hooks/useSoilRuleForm';
import type { SoilCard, SoilGroupKey, SoilTexture, StateReferenceDTO } from '../../types/parameters.types';
import type { SoilRuleUpdate } from '../../utils/soilRuleBuilder';
import { ValueInput } from '../Forms/ValueInput';
import { ClassLimitsFields } from './ClassLimitsFields';
import styles from './Sections.module.css';

interface SoilRuleModalProps {
	group: SoilGroupKey;
	reference: StateReferenceDTO;
	card: SoilCard | null;
	isSaving: boolean;
	onClose: () => void;
	onSubmit: (update: SoilRuleUpdate) => Promise<boolean>;
}

const KIND_OPTIONS = Object.entries(SOIL_KIND_LABELS).map(([value, label]) => ({ value, label }));

const CLAY_MODE_OPTIONS = [
	{ value: 'texture', label: 'Textura' },
	{ value: 'range', label: 'Faixa de argila' },
];

const TEXTURE_OPTIONS = (Object.keys(TEXTURE_LABELS) as SoilTexture[]).map((value) => ({
	value,
	label: TEXTURE_LABELS[value],
}));

export const SoilRuleModal = ({ group, reference, card, isSaving, onClose, onSubmit }: SoilRuleModalProps) => {
	const { form, errors, setField, submit } = useSoilRuleForm({ group, reference, card, onSubmit, onClose });
	const codeOptions = SOIL_GROUP_CODES[group].map((code) => ({ value: code, label: PARAM_LABELS[code] ?? code }));
	const basis = form.kind === 'Prem' ? 'P-rem' : 'argila';
	const isDependentEdit = !!card && card.kind !== 'fixed';

	return (
		<BaseModal
			isOpen
			wide
			title={card ? `Editar regra: ${card.title}` : 'Nova regra de interpretação'}
			subtitle={card?.meta}
			isLoading={isSaving}
			onClose={onClose}
			actions={<FormActions onCancel={onClose} onSave={submit} isLoading={isSaving} />}
		>
			<div className={styles.form}>
				{!card && (
					<div className={styles.formSection}>
						<FormSectionTitle>Regra</FormSectionTitle>
						<div className={styles.fields}>
							<FormField label="Parâmetro" required>
								<FormSelect value={form.code} options={codeOptions} onChange={(value) => setField('code', value)} />
							</FormField>
							<FormField label="Tipo de regra" required>
								<FormSelect
									value={form.kind}
									options={KIND_OPTIONS}
									onChange={(value) => setField('kind', value as typeof form.kind)}
								/>
							</FormField>
							{form.kind === 'clay' && (
								<FormField label="Organizar argila por" required>
									<FormSelect
										value={form.clayMode}
										options={CLAY_MODE_OPTIONS}
										onChange={(value) => setField('clayMode', value as typeof form.clayMode)}
									/>
								</FormField>
							)}
							{form.kind === 'clay' && form.clayMode === 'texture' && (
								<FormField label="Textura" required>
									<FormSelect
										value={form.texture}
										options={TEXTURE_OPTIONS}
										onChange={(value) => setField('texture', value as SoilTexture)}
									/>
								</FormField>
							)}
						</div>
					</div>
				)}
				<div className={styles.formSection}>
					<FormSectionTitle>Identificação</FormSectionTitle>
					<div className={styles.fields}>
						<FormField label="Nome" required error={errors.name}>
							<FormInput
								value={form.name}
								hasError={!!errors.name}
								onChange={(event) => setField('name', event.target.value)}
							/>
						</FormField>
						<FormField label="Faixa ideal" required error={errors.idealRange}>
							<FormInput
								value={form.idealRange}
								placeholder="Ex.: 18–24"
								hasError={!!errors.idealRange}
								onChange={(event) => setField('idealRange', event.target.value)}
							/>
						</FormField>
						<FormField label="Método" required error={errors.method}>
							<FormInput
								value={form.method}
								placeholder="Ex.: Mehlich-1"
								hasError={!!errors.method}
								onChange={(event) => setField('method', event.target.value)}
							/>
						</FormField>
						<FormField label="Unidade" required error={errors.unit}>
							<FormInput
								value={form.unit}
								placeholder="Ex.: mg/dm³"
								hasError={!!errors.unit}
								onChange={(event) => setField('unit', event.target.value)}
							/>
						</FormField>
						{usesBand(form) && (
							<>
								<FormField label={`Faixa de ${basis}: mínimo`} error={errors.minValue}>
									<ValueInput
										label="Faixa mínima"
										value={form.minValue}
										hasError={!!errors.minValue}
										onChange={(value) => setField('minValue', value)}
									/>
								</FormField>
								<FormField label={`Faixa de ${basis}: máximo`} error={errors.maxValue}>
									<ValueInput
										label="Faixa máxima"
										value={form.maxValue}
										hasError={!!errors.maxValue}
										onChange={(value) => setField('maxValue', value)}
									/>
								</FormField>
							</>
						)}
					</div>
					{isDependentEdit && (
						<p className={styles.hint}>Nome, método e unidade valem para todas as regras de {card.title}.</p>
					)}
				</div>
				<div className={styles.formSection}>
					<FormSectionTitle>Classes de interpretação</FormSectionTitle>
					<p className={styles.hint}>O limite final de uma classe é o início da classe seguinte.</p>
					<ClassLimitsFields classes={form.classes} onChange={(classes) => setField('classes', classes)} />
					{errors.classes && (
						<span className={styles.formError} role="alert">
							{errors.classes}
						</span>
					)}
				</div>
				{errors.form && (
					<span className={styles.formError} role="alert">
						{errors.form}
					</span>
				)}
			</div>
		</BaseModal>
	);
};
